import React, { useCallback, useEffect, useState } from "react"
import { useParams, useOutletContext } from "react-router-dom"
import { useSelector } from "react-redux"
import ProjectCalendar, {
  ProjectCalendarSkeleton,
} from "../../../../components/DashboardComponents/ProjectCalendar"
import { CreateTaskModal, SetupWorkingScheduleModal } from "../../../../components/Modals"
import { get } from "../../../../api/axios"
import endpoints from "../../../../api/endpoints"
import { getUserAvatarUrl } from "../../../../helpers/userAvatar"
import { getUserFullName } from "../../../../helpers/userDisplay"
import { USE_ADMIN_PROJECT_FIXTURES } from "../adminProjectMock"

const collaboratorDisplayName = collaborator =>
  getUserFullName(collaborator?.user || collaborator) || null

/**
 * Map GET /api/v1/tasks/calendar result into calendar event chips.
 * Uses end_date (fallback start_date) and collaborators[].user.
 */
const parseTaskDate = value => {
  if (!value) return null

  const dateOnly = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (dateOnly) {
    return new Date(
      Number(dateOnly[1]),
      Number(dateOnly[2]) - 1,
      Number(dateOnly[3])
    )
  }

  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const transformCalendarTasks = (tasks, currentUserId) => {
  if (!Array.isArray(tasks)) return []

  return tasks
    .map(task => {
      const taskDate = parseTaskDate(task.end_date || task.start_date)
      if (!taskDate) return null

      const day = taskDate.getDate()
      const month = taskDate.getMonth()
      const year = taskDate.getFullYear()

      const collaborators = Array.isArray(task.collaborators)
        ? task.collaborators
        : []

      const names = collaborators
        .map(collaboratorDisplayName)
        .filter(Boolean)

      const isMine = collaborators.some(
        c =>
          String(c?.user?.id || c?.user_id || "") === String(currentUserId || "")
      )

      let owner = "Unassigned"
      if (names.length === 1) owner = names[0]
      else if (names.length > 1) owner = `${names[0]} +${names.length - 1}`
      else if (task.total_collabs > 0) owner = `${task.total_collabs} collab${task.total_collabs === 1 ? "" : "s"}`

      const mappedCollabs = collaborators
        .map(collaborator => ({
          id: collaborator.id || collaborator.user?.id,
          userId: collaborator.user_id || collaborator.user?.id || '',
          email: collaborator.user?.email || collaborator.email || '',
          name: collaboratorDisplayName(collaborator),
          src: getUserAvatarUrl(collaborator?.user),
        }))
        .filter(collaborator => collaborator.name)

      const firstUser = collaborators[0]?.user
      const estimated = Number(task.estimated_hours) || 1
      const logged = Number(task.total_hours_logged ?? task.logged_hours) || 0
      const totalCollaborators = Number(task.total_collaborators)

      return {
        id: task.id,
        day,
        month,
        year,
        title: task.title || task.name || "Untitled Task",
        owner,
        color: isMine ? "blue" : "black",
        isMine,
        dueDate: taskDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
        description: task.description || "",
        collaborators: mappedCollabs,
        totalCollaborators: Number.isFinite(totalCollaborators)
          ? totalCollaborators
          : mappedCollabs.length,
        assignee: {
          name: names[0] || "Unassigned",
          avatar: getUserAvatarUrl(firstUser),
          role: collaborators[0]?.role || "Member",
          roleTone: isMine ? "blue" : "purple",
        },
        timeTracking: {
          total: estimated,
          logged,
          remaining: Math.max(0, estimated - logged),
        },
        sessionActivity: [],
        onGoingSessionId: task.on_going_session_id || null,
        on_going_session_id: task.on_going_session_id || null,
        onGoingSessionStatus: task.on_going_session_status || null,
        on_going_session_status: task.on_going_session_status || null,
        onGoingSessionUserStatus: task.on_going_session_user_status || null,
        on_going_session_user_status: task.on_going_session_user_status || null,
      }
    })
    .filter(Boolean)
}

const formatLocalDate = date => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

const ProjectCalendarTab = () => {
  const { projectId } = useParams()
  const {
    calendarCursor,
    project,
    isScheduleModalOpen,
    setIsScheduleModalOpen,
    registerTabRefresh,
  } = useOutletContext() || {}
  const currentUser = useSelector(state => state.auth.currentUser)
  const currentUserId = currentUser?.id
  const [events, setEvents] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [scheduleDate, setScheduleDate] = useState("")

  const fetchCalendarTasks = useCallback(async () => {
    if (!projectId) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    if (USE_ADMIN_PROJECT_FIXTURES) {
      setEvents([])
      setIsLoading(false)
      return
    }

    const year = calendarCursor?.year ?? new Date().getFullYear()
    const month = calendarCursor?.month ?? new Date().getMonth()

    const startDate = formatLocalDate(new Date(year, month, 1))
    const endDate = formatLocalDate(new Date(year, month + 1, 0))

    try {
      const response = await get(endpoints.getCalendarTasks, {
        params: {
          project_id: projectId,
          start_date: startDate,
          end_date: endDate,
          "with[]": ["collaborators"],
        },
      })

      if (!response) {
        setEvents([])
        setError("Failed to load calendar tasks")
        return
      }

      if (response?.data?.success) {
        const tasks = response.data.result || []
        setEvents(transformCalendarTasks(tasks, currentUserId))
      } else {
        setEvents([])
        setError(
          response?.data?.message || "Failed to fetch calendar tasks"
        )
      }
    } catch (err) {
      setEvents([])
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load calendar tasks"
      )
    } finally {
      setIsLoading(false)
    }
  }, [projectId, currentUserId, calendarCursor])

  useEffect(() => {
    fetchCalendarTasks()
  }, [fetchCalendarTasks])

  useEffect(() => {
    if (typeof registerTabRefresh !== "function") return undefined
    return registerTabRefresh(fetchCalendarTasks)
  }, [registerTabRefresh, fetchCalendarTasks])

  useEffect(() => {
    if (isScheduleModalOpen) setScheduleDate("")
  }, [isScheduleModalOpen])

  const closeScheduleModal = useCallback(() => {
    setIsScheduleModalOpen?.(false)
  }, [setIsScheduleModalOpen])

  const handleScheduleSaved = useCallback(() => {
    setIsScheduleModalOpen?.(false)
    fetchCalendarTasks()
  }, [setIsScheduleModalOpen, fetchCalendarTasks])

  return (
    <>
      {isLoading ? (
        <ProjectCalendarSkeleton />
      ) : error ? (
        <div className="project-calendar__error">
          <p>{error}</p>
        </div>
      ) : (
        <ProjectCalendar
          events={events}
          onDayClick={
            project?.is_current_user_active_member ? setScheduleDate : undefined
          }
          projectStartDate={project?.startDate || project?.start_date || ""}
          projectEndDate={project?.endDate || project?.end_date || ""}
          onTaskDeleted={taskId => {
            setEvents(prev =>
              prev.filter(evt => String(evt.id) !== String(taskId)),
            )
            fetchCalendarTasks()
          }}
        />
      )}
      <CreateTaskModal
        isOpen={Boolean(scheduleDate)}
        projectId={projectId || ""}
        project={project}
        lockedDate={scheduleDate}
        projectStartDate={project?.startDate || project?.start_date || ""}
        projectEndDate={project?.endDate || project?.end_date || ""}
        onClose={() => setScheduleDate("")}
        onSave={() => {
          setScheduleDate("")
          fetchCalendarTasks()
        }}
      />
      <SetupWorkingScheduleModal
        isOpen={Boolean(isScheduleModalOpen)}
        projectId={projectId || ""}
        project={project}
        projectStartDate={project?.startDate || project?.start_date || ""}
        projectEndDate={project?.endDate || project?.end_date || ""}
        onClose={closeScheduleModal}
        onSave={handleScheduleSaved}
      />
    </>
  )
}

export default ProjectCalendarTab
